import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { AuthSplitShell } from "@/components/auth/AuthSplitShell";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { authClient } from "@/lib/auth/auth-client";
import { getMyRoles } from "@/server/auth-access.functions";

// Plain-element recipes matching the reference app's auth screen exactly —
// no shadcn Input/Label/Button wrappers, since those inject their own
// shadow/radius/focus-ring defaults that the reference design doesn't have.
const INPUT_CLS =
 "mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-[14px] focus:outline-none focus:ring-2 focus:ring-primary/40";
const LABEL_CLS = "text-[12px] font-medium text-foreground";
const PRIMARY_BTN_CLS =
 "h-10 w-full rounded-md bg-primary text-primary-foreground text-[14px] font-medium hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60";

export const Route = createFileRoute("/admin-login")({
 head: () => ({ meta: [{ name: "robots", content: "noindex,nofollow" }] }),
 component: AdminLoginPage,
});

function AdminLoginPage() {
 const navigate = useNavigate();
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [showPassword, setShowPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 // If already signed in as admin, redirect immediately
 useEffect(() => {
 let cancelled = false;
 (async () => {
 try {
 const { data } = await authClient.getSession();
 if (!data?.user || cancelled) return;
 const roles = await getMyRoles();
 if (cancelled) return;
 if (roles.includes("admin")) {
 navigate({ to: "/dashboard/admin" });
 }
 } catch { /* ignore */ }
 })();
 return () => { cancelled = true; };
 }, [navigate]);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);
 setLoading(true);
 try {
 const { error: signInError, data: signInData } = await authClient.signIn.email({ email, password });
 if (signInError) {
 setError("Invalid credentials.");
 return;
 }
 if (signInData?.twoFactorRedirect) return; // second-factor challenge in progress
 const roles = await getMyRoles();
 if (!roles.includes("admin")) {
 await authClient.signOut();
 setError("This account does not have admin access.");
 return;
 }
 navigate({ to: "/dashboard/admin" });
 } catch {
 setError("Something went wrong. Please try again.");
 } finally {
 setLoading(false);
 }
 };

 return (
 <AuthSplitShell>
 <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2">
 <ShieldCheck className="h-4 w-4 text-primary" />
 Admin Access
 </div>

 <h1 className="text-2xl font-semibold tracking-tight">Sign in to admin</h1>
 <p className="mt-1 text-[13px] text-muted-foreground">
 Restricted area. Admin accounts are provisioned manually, there is no sign-up.
 </p>

 <form onSubmit={handleSubmit} className="mt-6 space-y-4">
 <div>
 <label htmlFor="admin-email" className={LABEL_CLS}>Email</label>
 <input
 id="admin-email"
 className={INPUT_CLS}
 type="email"
 autoComplete="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 autoFocus
 required
 />
 </div>
 <div>
 <label htmlFor="admin-password" className={LABEL_CLS}>Password</label>
 <div className="relative">
 <input
 id="admin-password"
 className={`${INPUT_CLS} pr-10`}
 type={showPassword ? "text" : "password"}
 autoComplete="current-password"
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 required
 minLength={6}
 />
 <button
 type="button"
 onClick={() => setShowPassword((v) => !v)}
 className="absolute right-0 top-0 mt-1 h-10 w-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
 aria-label={showPassword ? "Hide password" : "Show password"}
 tabIndex={-1}
 >
 {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
 </button>
 </div>
 </div>
 <button type="submit" className={PRIMARY_BTN_CLS} disabled={loading}>
 {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
 Sign in
 </button>
 </form>

 {error && <p className="mt-4 text-[13px] text-destructive text-center">{error}</p>}

 <p className="mt-6 text-center text-xs text-muted-foreground">
 <Link to="/" className="hover:text-foreground">← Back to home</Link>
 </p>
 </AuthSplitShell>
 );
}
